import { PRODUCT_PLACEHOLDERS } from "./product-placeholders";
// ---------------------------------------------------------------------------
// CLEANER PRODUCTS — mirrored from supplier (Trivantage) for our own site.
// Each product has its own internal detail page at /shop/cleaners/$slug
// so customers stay on our site instead of being redirected.
// ---------------------------------------------------------------------------

export type CleanerProduct = {
  id: string;
  slug: string;
  name: string;
  size: string;
  itemNumber: string;
  /** Short blurb shown on the listing card */
  blurb: string;
  /** Long description shown on the detail page (paragraphs) */
  description: string[];
  /** Bullet-point feature list */
  features: string[];
  /** Spec table key/value pairs */
  specs: { label: string; value: string }[];
  /** Product image URL (hosted by supplier CDN) */
  image: string;
  /** Optional SDS / safety data sheet PDF link */
  sdsUrl?: string;
  /** Optional product note ("Ground shipping only", etc.) */
  productNote?: string;
};

export const cleanerProducts: CleanerProduct[] = [
  {
    id: "303-fabric-guard-non-pfas-32oz",
    slug: "303-fabric-guard-non-pfas-32oz",
    name: "303® Fabric Guard™ Non-PFAS",
    size: "32-oz Trigger Sprayer",
    itemNumber: "213893",
    blurb: "Restores water & stain repellency to Sunbrella® and outdoor fabrics. PFAS-free formula.",
    description: [
      "Glen Raven, Inc. recommends non-PFAS 303 Fabric Guard for re-treatment of Sunbrella® marine, awning, and casual furniture fabric.",
      "The water-repellent spray is powerful enough to protect fabrics in the harshest outdoor environments, yet safe enough for the finest fabrics, leather, wool, and silk. On outdoor fabrics, non-PFAS 303 Fabric Guard recovers lost water repellency and stain resistance to factory-new levels. The protectant prevents leaks, protects against soiling on both water- and oil-based stains, hinders mildew formation, and protects against the sun's harmful UV rays.",
      "Proper maintenance with 303 Products will improve the longevity of your fabrics and save you money. Non-PFAS 303 Fabric Guard is simple to use — just spray on and let dry. Available here in a 32-ounce spray bottle. Covers 150–200 sq. ft. for lightweight fabrics and 80–150 sq. ft. for heavyweight fabrics.",
    ],
    features: [
      "Recommended by Glen Raven for Sunbrella® re-treatment",
      "PFAS-free formula",
      "Restores water repellency & stain resistance",
      "Hinders mildew formation",
      "UV protection",
      "Simple application — spray on, let dry",
    ],
    specs: [
      { label: "Product Category", value: "303 Fabric Guard" },
      { label: "Put Up", value: "32 oz. trigger sprayer" },
      { label: "Item #", value: "213893" },
      { label: "Coverage", value: "150–200 sq. ft. light / 80–150 sq. ft. heavy" },
    ],
    image: PRODUCT_PLACEHOLDERS.cleaner,
    productNote: "Ground shipping only.",
  },
  {
    id: "303-fabric-guard-non-pfas-1gal",
    slug: "303-fabric-guard-non-pfas-1gal",
    name: "303® Fabric Guard™ Non-PFAS",
    size: "1-gal. (128 oz.) Refill",
    itemNumber: "213891",
    blurb: "Bulk refill for shop use — re-treat large covers, awnings and bimini tops.",
    description: [
      "Glen Raven, Inc. recommends non-PFAS 303 Fabric Guard for re-treatment of Sunbrella® marine, awning, and casual furniture fabric.",
      "The water-repellent spray is powerful enough to protect fabrics in the harshest outdoor environments, yet safe enough for the finest fabrics, leather, wool, and silk. On outdoor fabrics, non-PFAS 303 Fabric Guard recovers lost water repellency and stain resistance to factory-new levels. The protectant prevents leaks, protects against soiling on both water- and oil-based stains, hinders mildew formation, and protects against the sun's harmful UV rays.",
      "Available here as a 1-gallon (128 oz.) refill — ideal for shops re-treating awnings, bimini tops and large covers. Covers 150–200 sq. ft. for lightweight fabrics and 80–150 sq. ft. for heavyweight fabrics per quart.",
    ],
    features: [
      "Bulk shop / contractor size",
      "Recommended for Sunbrella® re-treatment",
      "PFAS-free formula",
      "Restores factory-new water repellency",
      "Inhibits mildew & UV damage",
    ],
    specs: [
      { label: "Product Category", value: "303 Fabric Guard" },
      { label: "Put Up", value: "1 gal. (128 oz.) refill" },
      { label: "Item #", value: "213891" },
    ],
    image: PRODUCT_PLACEHOLDERS.cleaner,
    productNote: "Ground shipping only.",
  },
  {
    id: "303-fabric-guard-non-pfas-16oz",
    slug: "303-fabric-guard-non-pfas-16oz",
    name: "303® Fabric Guard™ Non-PFAS",
    size: "16-oz Trigger Sprayer",
    itemNumber: "213892",
    blurb: "Smaller bottle for spot re-treats on cushions, umbrellas and pillows.",
    description: [
      "Glen Raven, Inc. recommends non-PFAS 303 Fabric Guard for re-treatment of Sunbrella® marine, awning, and casual furniture fabric.",
      "Powerful enough for the harshest outdoor environments, yet safe for the finest fabrics, leather, wool and silk. Recovers lost water repellency and stain resistance, hinders mildew, and shields against UV.",
      "16-oz size is perfect for spot re-treating cushions, umbrellas, throw pillows and small canvas pieces. Covers about 75–100 sq. ft. on lightweight fabrics.",
    ],
    features: [
      "Convenient spot-treatment size",
      "PFAS-free formula",
      "Recommended for Sunbrella® re-treatment",
      "UV & mildew protection",
    ],
    specs: [
      { label: "Product Category", value: "303 Fabric Guard" },
      { label: "Put Up", value: "16 oz. trigger sprayer" },
      { label: "Item #", value: "213892" },
    ],
    image: PRODUCT_PLACEHOLDERS.cleaner,
    productNote: "Ground shipping only.",
  },
  {
    id: "303-multi-surface-cleaner-1gal",
    slug: "303-multi-surface-cleaner-1gal-refill",
    name: "303® Multi-Surface Cleaner",
    size: "1-gal. Refill (#30570)",
    itemNumber: "214116",
    blurb: "Gentle deep-clean for fabric, vinyl and clear vinyl windows. Bulk refill size.",
    description: [
      "303 Multi-Surface Cleaner is a premium cleaner specially formulated to clean all surfaces, while rinsing free of residue. The industrial-strength concentrate is made for all water-safe fabrics as well as metal, teak, resin, plastic, and vinyl. It contains no foam, fumes, odor, harsh chemicals, caustics, or acids.",
      "Ideal for awnings, marine canvas, fabric convertible tops, carpet, indoor and outdoor furniture, and upholstery. For spot cleaning, use diluted or full strength. 303 Multi-Surface Cleaner safely removes even the most difficult stains. For optimal results, pre-clean canvas before re-treating with 303 Fabric Guard.",
      "Available here in a 1-gallon refill. Covers about 20–35 sq. ft. per 16 oz. 303 Fabric Guard and 303 Fabric Cleaner are recommended for Sunbrella® by Glen Raven, Inc.",
    ],
    features: [
      "No foam, fumes, odor, caustics or acids",
      "Safe for fabric, metal, teak, resin, plastic, vinyl",
      "Use diluted or full strength for spot cleaning",
      "Recommended pre-treatment before 303 Fabric Guard",
    ],
    specs: [
      { label: "Product Category", value: "303 Multi-Surface Cleaner" },
      { label: "Put Up", value: "1 gal. refill" },
      { label: "Item #", value: "214116" },
    ],
    image: PRODUCT_PLACEHOLDERS.cleaner,
    productNote: "Do not use on Strataglass™ or CrystalClear clear vinyl.",
  },
  {
    id: "303-multi-surface-cleaner-16oz",
    slug: "303-multi-surface-cleaner-16oz-trigger",
    name: "303® Multi-Surface Cleaner",
    size: "16-oz Trigger Sprayer (#30445)",
    itemNumber: "214076",
    blurb: "Indoor / outdoor cleaner — safe on Sunbrella®, vinyl, plastic and painted surfaces.",
    description: [
      "303 Indoor/Outdoor Multi-Surface Cleaner is a premium, residue-free formula safe for all water-safe fabrics, metal, teak, resin, plastic and vinyl. No foam, no fumes, no odor — and no harsh chemicals, caustics or acids.",
      "Perfect for awnings, marine canvas, convertible tops, carpet, upholstery and indoor/outdoor furniture. For spot cleaning, use diluted or full strength. Pre-clean canvas before re-treating with 303 Fabric Guard for best results.",
      "Available here in a 16-oz trigger sprayer — convenient for everyday use around the home or boat.",
    ],
    features: [
      "Ready-to-use trigger sprayer",
      "No residue, no fumes, no harsh chemicals",
      "Safe on fabric, vinyl, plastic, metal, teak",
      "Recommended for Sunbrella® by Glen Raven",
    ],
    specs: [
      { label: "Product Category", value: "303 Multi-Surface Cleaner" },
      { label: "Put Up", value: "16 oz. trigger sprayer" },
      { label: "Item #", value: "214076" },
    ],
    image: PRODUCT_PLACEHOLDERS.cleaner,
    productNote: "Do not use on Strataglass™ or CrystalClear clear vinyl.",
  },
  {
    id: "303-multi-surface-cleaner-32oz",
    slug: "303-multi-surface-cleaner-32oz-trigger",
    name: "303® Multi-Surface Cleaner",
    size: "32-oz Trigger Sprayer (#30207)",
    itemNumber: "214080",
    blurb: "Larger trigger bottle of the everyday multi-surface cleaner.",
    description: [
      "Same trusted 303 Multi-Surface Cleaner formula in a larger 32-oz ready-to-use trigger sprayer. Premium, residue-free cleaner for all water-safe fabrics as well as metal, teak, resin, plastic, and vinyl.",
      "Industrial-strength concentrate with no foam, fumes, odor, harsh chemicals, caustics or acids. Ideal for awnings, marine canvas, fabric convertible tops, carpet, upholstery and indoor/outdoor furniture.",
      "For spot cleaning, use diluted or full strength. Pre-clean canvas before re-treating with 303 Fabric Guard for best results.",
    ],
    features: [
      "Larger 32-oz size for whole-cover cleaning",
      "Residue-free, low-foam formula",
      "Safe on fabric, vinyl, plastic, metal, teak",
      "Recommended pre-treatment before 303 Fabric Guard",
    ],
    specs: [
      { label: "Product Category", value: "303 Multi-Surface Cleaner" },
      { label: "Put Up", value: "32 oz. trigger sprayer" },
      { label: "Item #", value: "214080" },
    ],
    image: PRODUCT_PLACEHOLDERS.cleaner,
    productNote: "Do not use on Strataglass™ or CrystalClear clear vinyl.",
  },
  {
    id: "303-aerospace-protectant-2oz",
    slug: "303-aerospace-protectant-2oz",
    name: "303® Aerospace Protectant",
    size: "2-oz Pump Sprayer (#30302)",
    itemNumber: "214078",
    blurb: "UV protection for vinyl, rubber and plastic — extends life of clear vinyl windows.",
    description: [
      "303 Aerospace Protectant provides superior UV protection for vinyl, rubber, plastic, fiberglass gel-coat and finished leather. Helps prevent fading, cracking and discoloration caused by sun exposure.",
      "Industrial-strength formula recommended by leading marine, RV, and outdoor manufacturers. Restores like-new appearance and dries to a clean, dry, matte finish — never oily or greasy.",
      "Available here in a 2-oz pump sprayer — perfect for travel, sample use or spot treating clear vinyl windows on bimini tops and enclosures.",
    ],
    features: [
      "Premium UV protection",
      "Prevents fading, cracking & discoloration",
      "Dry, non-greasy matte finish",
      "Safe on vinyl, rubber, plastic, fiberglass & leather",
    ],
    specs: [
      { label: "Product Category", value: "303 Aerospace Protectant" },
      { label: "Put Up", value: "2 oz. pump sprayer" },
      { label: "Item #", value: "214078" },
    ],
    image: PRODUCT_PLACEHOLDERS.cleaner,
    productNote: "Do not use on Strataglass™ or CrystalClear clear vinyl.",
  },
];

export function getCleanerBySlug(slug: string): CleanerProduct | undefined {
  return cleanerProducts.find((p) => p.slug === slug);
}
